import * as React from "react";
import {
    Modal, Box, Typography, IconButton, Grid, TextField, Select, MenuItem,
    Divider, Stack, InputAdornment,
    Skeleton,
    Autocomplete
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import CtaButton from "../buttonWithArrow";
import { getPrivateJetListNew } from '../../redux/slices/common';
import { useDispatch, useSelector } from '../../redux/store';
import { lockScroll, unlockScroll } from "../../utils/scrollLock";
import { CurrencySimple, useResponsiveValue } from "../../utils/helper";
import { countries } from '../../data/countries';

function JetSkeletonCard() {
    return (
        <>
            <Box>
                <Skeleton
                    variant="rectangular"
                    height={100}
                    sx={{ borderRadius: "12px" }}
                />
                <Skeleton
                    variant="text"
                    width="60%"
                    height={18}
                    sx={{ mx: "auto", mt: 1 }}
                />
            </Box>
        </>
    );
}

// ---------- helpers ----------
const CURRENCY = {
    USD: { sign: "$", rate: 1, type: "string" },
    AED: { sign: "images/bannerImage/1758795455186.svg", rate: 3.67, type: "image" },
    // EUR: { sign: "€", rate: 0.92 },
};

const fmt = (amount, count) => {
    const total = (amount || 0) * (count || 0);
    return `${total.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
};

// pill look
const pillInputSx = {
    "& .MuiOutlinedInput-root": { borderRadius: 999 },
};

// stat pill
const statSx = {
    p: 2,
    borderRadius: 2.5,
    border: "1px solid #E6E8EC",
    bgcolor: "#fff",
    textAlign: "center",
};

const TEAL_BOTTOM = "#0D8196";

const jetTileSx = (active) => ({
    objectFit: "contain", borderRadius: 3, "&:hover": { border: "2px solid #0d8196" },
    border: active ? "2px solid #0d8196" : "none"
});

const jetLabelSx = (active) => ({
    textAlign: "center", mt: 0.75, fontWeight: 700, fontSize: 13,
    color: active ? TEAL_BOTTOM : "#111827",
});

const jetPriceSx = (active) => ({
    textAlign: "center", mt: 0.75, fontWeight: 700, fontSize: 13,
    color: active ? TEAL_BOTTOM : "#111827",
    display: "flex",
    justifyContent: "center",
    alignItems: 'center'
});

// ---------- main component ----------
export default function InvestmentSimulatorFlow({
    open,
    onClose,
    onInvest, // (payload) => void
    values,
    setValues,
    formLoader, step, setStep, amount, setAmount, priceData
}) {
    const dispatch = useDispatch();
    const [currency, setCurrency] = React.useState("AED");
    const [selected, setSelected] = React.useState(null);
    const [jets, setJets] = React.useState([]);
    const [loader, setLoader] = React.useState(true);
    const { privateJetNewRes } = useSelector(
        state => state.common
    );
    const parsedAmount = Number(String(amount).replace(/,/g, "")) || 0;
    const [error, setError] = React.useState({
        name: false,
        email: false,
        countryCode: false,
        phone: false
    });
    // move hook call here
    const responsiveFontSize = useResponsiveValue({ xs: 16, sm: 16, lg: 16 });
    const responsiveSelectFont = useResponsiveValue({ xs: 15, sm: 15, lg: 15 });
    const responsiveSelectPrice = useResponsiveValue({ xs: 13, sm: 13, lg: 13 });
    const responsiveIconSize = useResponsiveValue({ xs: 18, sm: 26 });

    React.useEffect(() => {
        if (open) lockScroll();
        else unlockScroll();

        return () => unlockScroll(); // cleanup if component unmounts
    }, [open]);

    React.useEffect(() => {
        setLoader(true);
        dispatch(getPrivateJetListNew({ amount: amount, currency: currency.toLowerCase() }));
    }, [dispatch, amount])

    // handle response
    React.useEffect(() => {
        if (!privateJetNewRes) return;      // wait until we have a response
        setLoader(false);                        // stop loading no matter success/fail        
        if (privateJetNewRes.status === 1) {
            setJets(privateJetNewRes.responseData ?? []);
        } else {
            setJets([]);
        }
    }, [privateJetNewRes]);

    // eligible jets: can buy at least 1 share
    const eligible = React.useMemo(() => {
        // const rate = CURRENCY[currency]?.rate;
        // const usd = parsedAmount / rate;
        return jets.filter(j => amount >= j?.price);
    }, [jets, amount, currency]);

    const resetAll = () => {
        setStep(1);
        setSelected(null);
        setValues({ ...values, shares: 1 })
        setValues({ ...values, shares: 1 })
        setAmount("");
        setCurrency("AED");
    };

    const closeAll = () => { onClose(); setTimeout(resetAll, 300); };

    const errors = React.useMemo(() => {
        const errs = {};

        // User name
        if (!values.name.trim()) {
            errs.name = "Name is required";
        }

        // Email
        if (!values.email.trim()) {
            errs.email = "Email is required";
        } else {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(values.email)) {
                errs.email = "Enter a valid email address";
            }
        }

        // Phone
        if (!values.phone.trim()) {
            errs.phone = "Phone number is required";
        } else {
            const phoneRegex = /^[0-9]{6,15}$/; // 6–15 digits
            if (!phoneRegex.test(values.phone)) {
                errs.phone = "Enter a valid phone number";
            }
        }

        return errs;
    }, [values]);

    const isValid = React.useMemo(() => Object.keys(errors).length === 0, [errors]);

    const handleSubmit = () => {
        setError(
            {
                name: true,
                email: true,
                countryCode: true,
                phone: true
            }
        );
        if (!isValid) return;
        setError(
            {
                name: false,
                email: false,
                countryCode: false,
                phone: false
            }
        );
        onInvest?.(values);
    };

    return (
        <Modal open={open}
            onClose={(event, reason) => {
                if (reason !== "backdropClick") closeAll(); // prevent outside click close
            }}
            sx={{ display: "flex", alignItems: "center", justifyContent: "center", p: 2 }}>
            <Box sx={{ position: "relative", width: 820, maxWidth: "95vw", bgcolor: "#fff", borderRadius: 4, p: 3 }}>
                {/* close */}
                <IconButton onClick={closeAll} sx={{ position: "absolute", top: 8, right: 8 }}>
                    <CloseRoundedIcon />
                </IconButton>

                {/* ---------------------- STEP HEADER ---------------------- */}
                {step !== 2 ? (
                    <>
                        <Typography className="jura" sx={{ fontWeight: 800, fontSize: 24, textAlign: "center" }}>
                            Investment calculator simulator
                        </Typography>
                        {
                            step !== 3 &&
                            <Typography sx={{ color: "#6b7280", textAlign: "center", mt: 1, mb: 2 }}>
                                Enter your amount to see tailored options where you can invest and grow with RSL.
                            </Typography>
                        }
                    </>
                ) : (
                    <Typography className="jura" sx={{ fontWeight: 800, fontSize: 28, textAlign: "center", mb: 1 }}>
                        {selected?.title}
                    </Typography>
                )}

                {/* ---------------------- STEP 1 & 2 ---------------------- */}
                {step !== 2 && step === 1 && (
                    <Box sx={{ border: "1px solid #edf0f3", borderRadius: 2.5, p: 2 }}>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} md={8}>
                                <Typography sx={{ fontWeight: 700, mb: 0.5 }}>Amount</Typography>
                                <Select
                                    fullWidth
                                    displayEmpty
                                    value={amount ?? ""}
                                    onChange={(e) => setAmount(e.target.value)}
                                    sx={{
                                        ...pillInputSx,
                                        "& fieldset": { borderRadius: 999 },
                                    }}
                                    renderValue={(selected) => {
                                        if (!selected) {
                                            return (
                                                <Box sx={{ color: "rgba(0,0,0,0.5)" }}>
                                                    Select Amount
                                                </Box>
                                            );
                                        }
                                        return (
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                                {CurrencySimple(
                                                    null,
                                                    null,
                                                    responsiveSelectFont,
                                                    CURRENCY[currency]?.type,
                                                    CURRENCY[currency]?.type === "string"
                                                        ? CURRENCY[currency]?.sign
                                                        : null
                                                )}
                                                {selected}
                                            </Box>
                                        );
                                    }}
                                >
                                    {/* placeholder item (hidden but keeps accessibility) */}
                                    <MenuItem value="">
                                        <em>Select Amount</em>
                                    </MenuItem>

                                    {priceData?.map((c) => (
                                        <MenuItem key={c} value={c}>
                                            <Box sx={{ display: "flex", alignItems: "center" }}>
                                                {CurrencySimple(
                                                    null,
                                                    null,
                                                    responsiveSelectFont,
                                                    CURRENCY[currency]?.type,
                                                    CURRENCY[currency]?.type === "string"
                                                        ? CURRENCY[currency]?.sign
                                                        : null
                                                )}
                                                {c}
                                            </Box>
                                        </MenuItem>
                                    ))}
                                </Select>
                                {/* <TextField
                                    fullWidth
                                    value={amount}
                                    onChange={(e) => {
                                        const raw = e.target.value.replace(/\D/g, "");
                                        setAmount(raw);
                                    }}
                                    placeholder="Enter Amount you want to invest"
                                    sx={pillInputSx}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                {CurrencySimple(null, "#0000008a", responsiveFontSize, CURRENCY[currency]?.type,
                                                    CURRENCY[currency]?.type === "string" ? CURRENCY[currency]?.sign : null)}
                                            </InputAdornment>

                                        ),
                                    }}
                                /> */}
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Typography sx={{ fontWeight: 700, mb: 0.5 }}>Currency</Typography>
                                <Select
                                    fullWidth
                                    value={currency}
                                    onChange={(e) => setCurrency(e.target.value)}
                                    sx={{ ...pillInputSx, "& fieldset": { borderRadius: 999 } }}
                                    renderValue={(v) => (
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                            {CurrencySimple(null, null, responsiveSelectFont, CURRENCY[currency]?.type,
                                                CURRENCY[currency]?.type === "string" ? CURRENCY[currency]?.sign : null)}
                                            {v}
                                        </Box>
                                    )}
                                >
                                    {Object.keys(CURRENCY).map((c) => (
                                        <MenuItem key={c} value={c}>
                                            <Box sx={{ display: "flex", alignItems: "center" }}>
                                                {CURRENCY[c].type === "string" ? (
                                                    <>{CURRENCY[c].sign}</>
                                                ) : (
                                                    <img
                                                        src={`${process.env.IMAGE_BASE_URL}/${String(CURRENCY[c].sign)}`}
                                                        alt="currency"
                                                        style={{ width: "1em", height: "1em", objectFit: "contain" }}
                                                    />
                                                )}
                                                {c}
                                            </Box>
                                        </MenuItem>
                                    ))}
                                </Select>
                            </Grid>
                        </Grid>

                        {parsedAmount > 0 && (
                            <>
                                <Divider sx={{ my: 2 }} />
                                <Typography sx={{ fontWeight: 600, mb: 1 }}>
                                    These are the options you can invest in <Box component="span" sx={{ color: "#9CA3AF", fontWeight: 500 }}>(Choose one to continue)</Box>
                                </Typography>

                                <Box
                                    sx={{
                                        display: "flex",
                                        gap: 2,
                                        overflowX: "auto",
                                        py: 1,
                                        px: 1,
                                        mt: 2,
                                        "&::-webkit-scrollbar": {
                                            height: 6,
                                        },
                                        "&::-webkit-scrollbar-thumb": {
                                            backgroundColor: "#ccc",
                                            borderRadius: 3,
                                        },
                                    }}
                                >
                                    {loader &&
                                        [...Array(6)].map((_, i) => (
                                            <Box key={i} sx={{ flex: "0 0 auto", width: 150 }}>
                                                <JetSkeletonCard />
                                            </Box>
                                        ))}

                                    {!loader && eligible.length === 0 && (
                                        <Box sx={{ color: "#9CA3AF", textAlign: "center", py: 2, flex: "0 0 100%" }}>
                                            No options match this amount yet. Try a higher amount.
                                        </Box>
                                    )}

                                    {!loader &&
                                        eligible.map((j) => {
                                            const active = selected?._id === j?._id;
                                            return (
                                                <Box
                                                    key={j?._id}
                                                    sx={{
                                                        flex: "0 0 auto",
                                                        width: 150,
                                                        cursor: "pointer",
                                                    }}
                                                    onClick={() => {
                                                        setSelected(j);
                                                        setStep(2);
                                                        setValues({
                                                            ...values,
                                                            jetId: j?._id,
                                                            sharePrice: j?.sharePrice,
                                                        });
                                                    }}
                                                >
                                                    <Box
                                                        component="img"
                                                        alt={j?.title}
                                                        src={`${process.env.IMAGE_BASE_URL}/${j?.image}`}
                                                        sx={jetTileSx(active)}
                                                    />
                                                    <Typography sx={jetLabelSx(active)}>{j?.title}</Typography>
                                                    <Typography sx={jetPriceSx(active)}>({CurrencySimple(null, active ? "#0D8196" : "#111827", responsiveSelectPrice, CURRENCY[currency]?.type,
                                                        CURRENCY[currency]?.type === "string" ? CURRENCY[currency]?.sign : null)}{j?.price})</Typography>
                                                </Box>
                                            );
                                        })}
                                </Box>

                            </>
                        )}
                    </Box>
                )}

                {/* ---------------------- STEP 3 (DETAIL) ---------------------- */}
                {step === 2 && selected && (
                    <>
                        <Stack direction="row" spacing={2} justifyContent="center" sx={{ mb: 2, flexWrap: "wrap", gap: { xs: "8px", sm: "0px" } }}>
                            <Chiplet label={`Model : ${selected?.year}`} />
                            <Chiplet label={`Capacity : ${selected?.capacity}`} />
                            <Chiplet label={`Type : ${selected?.formattedType}`} />
                        </Stack>


                        <Grid container spacing={2}>
                            <Grid item xs={6} md={6}>
                                <Stat title="Total Shares" value={String(selected.totalShares)} teal />
                            </Grid>
                            <Grid item xs={6} md={6}>
                                <Box sx={{ ...statSx }}>
                                    <Typography sx={{ color: "#6b7280", fontWeight: 700, mb: 0.5, fontSize: { xs: 12, sm: 16 } }}>
                                        Shares you can buy
                                    </Typography>
                                    <Stack direction="row" alignItems="center" justifyContent="center" spacing={2}>
                                        <IconButton size="small" onClick={() => setValues({ ...values, shares: Math.max(1, values?.shares - 1) })}>–</IconButton>
                                        <Typography sx={{ fontWeight: 800, fontSize: { xs: 18, sm: 28 }, minWidth: 32, textAlign: "center", color: "#8dd2c9" }}>
                                            {String(values?.shares).padStart(2, "0")}
                                        </Typography>
                                        <IconButton size="small" onClick={() => setValues({ ...values, shares: Math.min(selected.totalShares, values?.shares + 1) })}> +</IconButton>
                                    </Stack>
                                </Box>
                            </Grid>

                            {
                                selected?.formatterPricePeriod?.map((item, index) => (
                                    <Grid key={index} item xs={6} md={6}>
                                        <Stat title={item?.label} value={fmt(item?.value, Number(values?.shares))} type={CURRENCY[currency]?.type} sign={CURRENCY[currency]?.sign} responsiveIconSize={responsiveIconSize} teal />
                                    </Grid>
                                ))
                            }
                        </Grid>

                        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}>
                            <CtaButton label="Back" iconPosition="start" onClick={() => setStep(step - 1)} variant="secondary" sx={{ boxShadow: "none !important" }} endIconSrc="/img/header/VectorLeft.svg" />
                            <CtaButton label="Invest Now" onClick={() => setStep(3)} />
                        </Box>
                    </>
                )}

                {
                    step === 3 &&
                    <Box sx={{ p: 2 }}>
                        <Typography sx={{ fontWeight: 700 }}>Personal Details</Typography>
                        <Grid container spacing={2} sx={{ mt: 0 }}>
                            <Grid item xs={12} md={6}>
                                <Typography sx={{ fontWeight: 500, mb: 0.75 }}>Name</Typography>
                                <TextField
                                    fullWidth
                                    placeholder="Enter Name"
                                    name="name"
                                    value={values.name}
                                    error={error.name && !!errors.name}
                                    helperText={error.name && errors.name ? errors.name : " "}
                                    onChange={(e) => setValues({ ...values, name: e.target.value })}
                                    sx={pillInputSx}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Typography sx={{ fontWeight: 500, mb: 0.75 }}>Email</Typography>
                                <TextField
                                    fullWidth
                                    placeholder="Enter Email"
                                    value={values.email}
                                    name="email"
                                    error={error.email && !!errors.email}
                                    helperText={error.email && errors.email ? errors.email : " "}
                                    onChange={(e) => setValues({ ...values, email: e.target.value })}
                                    sx={pillInputSx}
                                />

                            </Grid>
                            <Grid item xs={12}>
                                <Typography sx={{ fontWeight: 500, mb: 0.75 }}>Phone</Typography>
                                <Box sx={{ display: "flex", mb: 2 }}>
                                    {/* Country Code Selector */}
                                    <Autocomplete
                                        disableClearable
                                        options={countries}
                                        getOptionLabel={(option) => `${option.code} (+${option.phone})`}
                                        filterOptions={(options, { inputValue }) =>
                                            options.filter(
                                                (option) =>
                                                    option.label.toLowerCase().includes(inputValue.toLowerCase()) ||
                                                    option.code.toLowerCase().includes(inputValue.toLowerCase()) ||
                                                    option.phone.includes(inputValue.replace("+", ""))
                                            )
                                        }
                                        value={countries.find((c) => c.phone === values.countryCode) || countries[0]}
                                        onChange={(e, value) => {
                                            if (value) setValues({ ...values, countryCode: value.phone });
                                        }}
                                        sx={{
                                            width: "35%",
                                            "& .MuiOutlinedInput-root": {
                                                borderRadius: "999px 0 0 999px",
                                            },
                                        }}
                                        renderOption={(props, option) => (
                                            <li {...props}>
                                                <span style={{ marginRight: 8 }}>
                                                    {String.fromCodePoint(
                                                        ...[...option.code.toUpperCase()].map((c) => 127397 + c.charCodeAt())
                                                    )}
                                                </span>
                                                {option.code} (+{option.phone})
                                            </li>
                                        )}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                variant="outlined"
                                                sx={{
                                                    "& .MuiOutlinedInput-root": {
                                                        borderRadius: "999px 0 0 999px",
                                                    },
                                                }}
                                            />
                                        )}
                                    />

                                    {/* Phone Number Input */}
                                    <TextField
                                        fullWidth
                                        placeholder="Enter Phone"
                                        value={values.phone}
                                        name="phone"
                                        onChange={(e) => setValues({ ...values, phone: e.target.value })}
                                        error={error.phone && !!errors.phone}
                                        helperText={error.phone && errors.phone ? errors.phone : " "}
                                        sx={{
                                            "& .MuiOutlinedInput-root": {
                                                borderRadius: "0 999px 999px 0", // rounded right side
                                                bgcolor: "#fff",
                                            }
                                        }}
                                    />
                                </Box>
                            </Grid>

                        </Grid>
                        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}>
                            <CtaButton label="Back" iconPosition="start" onClick={() => setStep(step - 1)} variant="secondary" sx={{ boxShadow: "none !important" }} endIconSrc="/img/header/VectorLeft.svg" />
                            <CtaButton label="Invest Now" loader={formLoader} onClick={handleSubmit} />
                        </Box>
                    </Box>
                }
            </Box>
        </Modal >
    );
}

// ---------- tiny subcomponents ----------
// Replace your Chiplet with this
function Chiplet({ label }) {
    return (
        <Box
            sx={{
                display: "inline-flex",
                alignItems: "center",
                whiteSpace: "nowrap",
                px: 2.5,
                py: 1,
                borderRadius: 2.5, // ≈10px
                // gradient outline over white background
                border: "2px solid transparent",
                background:
                    "linear-gradient(#fff,#fff) padding-box, linear-gradient(180deg,#8AD9D9 0%, #0D8196 100%) border-box",
                color: "#0D8196",
                fontWeight: 600,
                fontSize: { xs: 14, sm: 16 }, // adjust to taste
                lineHeight: 1.2,
            }}
        >
            {label}
        </Box>
    );
}


function Stat({ title, value, type, sign, responsiveIconSize, teal = false }) {
    return (
        <Box sx={statSx}>
            <Typography sx={{ color: "#000", fontWeight: 700, mb: 0.5, fontSize: { xs: 12, sm: 16 } }}>{title}</Typography>
            <Typography sx={{ fontWeight: 900, fontSize: { xs: 18, sm: 28 }, color: "#8dd2c9" }}>
                {
                    type && sign ?
                        CurrencySimple(value, "#8dd2c9", responsiveIconSize, type,
                            type === "string" ? sign : null)
                        : value
                }
            </Typography>
        </Box>
    );
}
